import React, { useState } from 'react';
import { Box, Button, Flex } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { TableNameE } from 'shared/api/types/common';
import PageTitle from 'shared/views/admin/PageTitle';
import { useGetColumns } from './constants';
import { useParams } from 'react-router-dom';
import { PathsParams, PathsE, ModuleE } from 'shared/routes';
import { useGet3thPartyServices, useGetOneService } from 'shared/api/requests/services';
import { DialogMessageDTO, DialogMessagesT } from 'shared/api/types/dialog_messages';
import Card from 'shared/components/horizon-ui/card/Card';
import MuiIcon from 'components/MuiIcon';
import DragableTable from 'shared/components/table/DragableTable';
import CreateMessageButton from './CreateMessageButton';

const Index = () => {
  const { serviceId } = useParams<PathsParams[PathsE.DIALOG_MESSAGES]>();
  const { t } = useTranslation();

  const { data: currentService } = useGetOneService({
    pathParams: { serviceId },
    params: undefined
  });


  const {
    useDeleteDialogMessage,
    useDeleteDialogMessages,
    useGetSocketDialogMessages,
    useUpdateOneDialogMessage,
    useRegenerateDialogMessage,
    useChangeOrderDialogMessage,
    useApplyEditDialogMessage,
    useSkipAllDelay,
  } = useGet3thPartyServices({
    domain: currentService?.domain,
    token: currentService?.token,
    module: ModuleE.DIALOG_MESSAGES
  });
  const { mutateAsync: deleteMutation, isPending: isDeletePending } = useDeleteDialogMessage();
  const { mutateAsync: deleteMessagesMutation, isPending: isDeleteMessagesPending } = useDeleteDialogMessages();
  const { mutateAsync: updateMutation } = useUpdateOneDialogMessage();
  const { mutateAsync: applyEditDialogMessage } = useApplyEditDialogMessage();
  const { mutate: changeOrderMutation } = useChangeOrderDialogMessage();
  const { mutate: regenerateDialogMutation, isPending: isPendingRegenerateDialogMutation } = useRegenerateDialogMessage();
  const { mutate: skipAllDelay, isPending: isPendingSkipAllDelay } = useSkipAllDelay();

  const [selectedMessages, setSelectedMessages] = useState<Record<string, boolean>>({});

  const handleDeleteSelected = async () => {
    await deleteMessagesMutation({ ids: Object.keys(selectedMessages).map(Number) });
    setSelectedMessages({});
  };

  const handleDelete = (messageId: DialogMessageDTO['id']) => {
    return deleteMutation({ messageId });
  };

  const handleUpdate = async (messageId: DialogMessageDTO['id'], newText: string) => {
    const newEditTime = moment().format('YYYY-MM-DDTHH:mm:ss');

    await updateMutation({
      messageId,
      newEditTime,
      newStatus: 'Edit',
      newText,
    });
    await applyEditDialogMessage({})
  };

  const onChangeOrder = (messageId: string | number, order: number) => {
    changeOrderMutation({
      messageId: Number(messageId),
      order,
    });
  };

  const columns = useGetColumns({
    handleDelete,
    handleUpdate,
    isDeletePending,
  });

  const btnProps = {
    color: "white",
    _hover: {
      bgColor: "brand.400",
    },
    bgColor: "brand.500",
    borderRadius: "8px",
    ml: "10px"
  };

  return (
    <Box h={'100%'}>
      <PageTitle title={t('services.Dialog messages')} />
      <Card borderRadius="20px" p="30px" shadow="md">
        <Flex alignItems="center" mb="20px">
          <CreateMessageButton />
          <Button
            isLoading={isPendingRegenerateDialogMutation}
            leftIcon={<MuiIcon name="refresh" color="white" />}
            onClick={() => regenerateDialogMutation({})}
            {...btnProps}
          >
            {t('buttons.regenerateDialog')}
          </Button>
          {!!Object.keys(selectedMessages).length && (
            <Button
              isLoading={isDeleteMessagesPending}
              leftIcon={<MuiIcon name="delete" color="white" />}
              bgColor="gray.100"
              backgroundColor="red.500"
              _hover={{
                background: "red.700",
              }}
              onClick={() => handleDeleteSelected()}
              color="white"
              borderRadius="8px"
              ml="10px"
            >
              {t('buttons.deleteSelected')}
            </Button>
          )}
          <Button
            isLoading={isPendingSkipAllDelay}
            leftIcon={<MuiIcon name="skip_next" color="white" />}
            onClick={() => skipAllDelay({})}
            {...btnProps}
          >
            {t('buttons.skipAllDelay')}
          </Button>
        </Flex>
        <DragableTable<DialogMessagesT>
          query={useGetSocketDialogMessages as FixMeLater}
          onChangeOrder={onChangeOrder}
          columns={columns}
          filterFields={[]}
          tableName={TableNameE.DIALOG_MESSAGES}
          onRowSelectionChange={setSelectedMessages}
          rowSelection={selectedMessages}
          disableSearch
        />
      </Card>
    </Box >
  );
}

export default Index;
